import { BuildData, KnightKey, NimueSelection } from '../types';
import { SWORN_DATA, ALL_BLESSINGS_MAP } from '../gameData';
import { constructBuildData } from '../utils/buildHelpers';

export interface AnvilState { mode: 'single' | 'double'; upgrades: string[] }
export interface MorganaState { mode: 'single' | 'double'; modifiers: string[] }
export interface MerlinState { mode: 'single' | 'double'; upgrades: string[] }
export type SourceState = { [key: string]: 'A' | 'B' };

export interface BuildState {
    selectedKnight: KnightKey | null;
    selectedWeapon: string | null;
    selectedSpell: string | null;
    selectedBlessings: Set<string>;
    
    nimueSelection: NimueSelection;
    anvil: AnvilState;
    morgana: MorganaState;
    merlin: MerlinState;
    source: SourceState;

    currentBuildId: string | null;
    currentBuildName: string | null;
    viewedBlessing: string | null;
}

export type BuildAction = 
  | { type: 'SET_KNIGHT'; knight: KnightKey | null }
  | { type: 'SET_WEAPON'; weapon: string | null }
  | { type: 'SET_SPELL'; spell: string | null }
  | { type: 'TOGGLE_BLESSING'; blessing: string }
  | { type: 'CLEAR_BLESSINGS' }
  
  | { type: 'SET_ANVIL_MODE'; mode: 'single' | 'double' }
  | { type: 'TOGGLE_ANVIL_UPGRADE'; upgrade: string }
  | { type: 'SET_ANVIL_UPGRADES'; upgrades: string[] }

  | { type: 'SET_MORGANA_MODE'; mode: 'single' | 'double' }
  | { type: 'TOGGLE_MORGANA_MODIFIER'; modifier: string }
  | { type: 'SET_MORGANA_MODIFIERS'; modifiers: string[] }

  | { type: 'SET_MERLIN_MODE'; mode: 'single' | 'double' }
  | { type: 'TOGGLE_MERLIN_UPGRADE'; upgrade: string }
  | { type: 'SET_MERLIN_UPGRADES'; upgrades: string[] }

  | { type: 'TOGGLE_NIMUE_OPTION'; option: 'option1' | 'option2' }
  | { type: 'SET_NIMUE_SELECTION'; selection: NimueSelection }

  | { type: 'TOGGLE_SOURCE_OPTION'; stoneKey: string }
  | { type: 'SET_SOURCE_OPTIONS'; options: SourceState }
  
  // Build Management
  | { type: 'LOAD_BUILD'; build: BuildData }
  | { type: 'NEW_BUILD'; defaultSourceOptions: SourceState }
  | { type: 'SET_VIEWED_BLESSING'; blessing: string | null }
  | { type: 'SET_BUILD_META'; id: string; name: string };


// --- Helper Reducer Logic ---
const toggleSet = (set: Set<string>, item: string): Set<string> => {
    const newSet = new Set(set);
    if (newSet.has(item)) newSet.delete(item);
    else newSet.add(item);
    return newSet;
};

const toggleArray = (arr: string[], item: string, mode: 'single' | 'double'): string[] => {
    const count = arr.filter(x => x === item).length;
    
    if (mode === 'single') {
        if (count > 0) return []; 
        return [item];
    } else {
        // Double mode
        if (count === 2) {
            const idx = arr.indexOf(item);
            const newArr = [...arr];
            newArr.splice(idx, 1);
            return newArr;
        }
        if (count === 1) {
            if (arr.length < 2) {
                return [...arr, item];
            } else {
                return [item, item];
            }
        }
        // count === 0
        if (arr.length < 2) {
            return [...arr, item];
        } else {
            // FIFO: Keep the second item (index 1) and add the new one
            return [arr[1], item];
        }
    }
};


// --- Reducer ---
export const buildReducer = (state: BuildState, action: BuildAction): BuildState => {
    switch (action.type) {
        case 'SET_KNIGHT': {
            const knight = action.knight;
             // Automatically select the first weapon and spell for the new knight
             let newWeapon: string | null = null;
             let newSpell: string | null = null;

            if (knight && SWORN_DATA.classes[knight]) {
                const weapons = Object.keys(SWORN_DATA.classes[knight].weapons);
                const spells = Object.keys(SWORN_DATA.classes[knight].spells);
                if (weapons.length > 0) newWeapon = weapons[0];
                if (spells.length > 0) newSpell = spells[0];
            }

            return {
                ...state,
                selectedKnight: knight,
                selectedWeapon: newWeapon,
                selectedSpell: newSpell,
                // Reset dependent modules
                nimueSelection: { option1: null, option2: null },
                anvil: { ...state.anvil, upgrades: [] },
                morgana: { ...state.morgana, modifiers: [] }
                // Merlin and Source usually global or less dependent? 
                // Previous code reset Merlin on NEW build, but not on Knight switch?
                // Wait, previous code: handleNewBuild resets EVERYTHING.
                // setSelectedKnight resets Nimue, Anvil, Morgana. NOT Merlin or Source.
            };
        }
        case 'SET_WEAPON':
            return {
                ...state,
                selectedWeapon: action.weapon,
                nimueSelection: { option1: null, option2: null },
                anvil: { ...state.anvil, upgrades: [] }
            };
        case 'SET_SPELL':
            return {
                ...state,
                selectedSpell: action.spell
            };
            
        case 'TOGGLE_BLESSING': {
            const blessingName = action.blessing;
            const newSet = new Set(state.selectedBlessings);
            const blessingData = ALL_BLESSINGS_MAP.get(blessingName);

            if (newSet.has(blessingName)) {
                newSet.delete(blessingName);
            } else {
                 if (blessingData) {
                    const abilityTypes = ['Light Attack', 'Heavy Attack', 'Spell', 'Dash', 'Companion', 'Ultimate'];
                    if (abilityTypes.includes(blessingData.type)) {
                         for (const existingName of newSet) {
                            const existingData = ALL_BLESSINGS_MAP.get(existingName);
                            if (existingData && existingData.type === blessingData.type) {
                                newSet.delete(existingName);
                            }
                        }
                    }
                }
                newSet.add(blessingName);
            }
            
            // Logic for viewed blessing update is usually side-effect of UI interaction, 
            // but if we toggle off the viewed one, we should probably clear it.
            let newViewed = state.viewedBlessing;
            if (state.viewedBlessing === blessingName && !newSet.has(blessingName)) {
                newViewed = null;
            } else if (newSet.has(blessingName)) {
                newViewed = blessingName;
            }

            return {
                ...state,
                selectedBlessings: newSet,
                viewedBlessing: newViewed
            };
        }
        case 'CLEAR_BLESSINGS':
            return { ...state, selectedBlessings: new Set(), viewedBlessing: null };

        // --- Anvil ---
        case 'SET_ANVIL_MODE':
            return { ...state, anvil: { ...state.anvil, mode: action.mode } };
        case 'TOGGLE_ANVIL_UPGRADE':
            return { ...state, anvil: { ...state.anvil, upgrades: toggleArray(state.anvil.upgrades, action.upgrade, state.anvil.mode) } };
        case 'SET_ANVIL_UPGRADES':
            return { ...state, anvil: { ...state.anvil, upgrades: action.upgrades } };

        // --- Morgana ---
        case 'SET_MORGANA_MODE':
            return { ...state, morgana: { ...state.morgana, mode: action.mode } };
        case 'TOGGLE_MORGANA_MODIFIER':
             return { ...state, morgana: { ...state.morgana, modifiers: toggleArray(state.morgana.modifiers, action.modifier, state.morgana.mode) } };
        case 'SET_MORGANA_MODIFIERS':
             return { ...state, morgana: { ...state.morgana, modifiers: action.modifiers } };

        // --- Merlin ---
        case 'SET_MERLIN_MODE':
             return { ...state, merlin: { ...state.merlin, mode: action.mode } };
        case 'TOGGLE_MERLIN_UPGRADE':
             return { ...state, merlin: { ...state.merlin, upgrades: toggleArray(state.merlin.upgrades, action.upgrade, state.merlin.mode) } };
        case 'SET_MERLIN_UPGRADES':
             return { ...state, merlin: { ...state.merlin, upgrades: action.upgrades } };

        // --- Nimue ---
        case 'TOGGLE_NIMUE_OPTION': {
            const { option } = action;
            const current = state.nimueSelection[option];
            const next = current === null ? 'A' : current === 'A' ? 'B' : null;
            return { ...state, nimueSelection: { ...state.nimueSelection, [option]: next } };
        }
        case 'SET_NIMUE_SELECTION':
            return { ...state, nimueSelection: action.selection };

        // --- Source ---
        case 'TOGGLE_SOURCE_OPTION': {
            const nextVal = state.source[action.stoneKey] === 'A' ? 'B' : 'A';
            return { ...state, source: { ...state.source, [action.stoneKey]: nextVal } };
        }
        case 'SET_SOURCE_OPTIONS':
            return { ...state, source: action.options };

        case 'SET_VIEWED_BLESSING':
            return { ...state, viewedBlessing: action.blessing };

        case 'SET_BUILD_META':
            return { ...state, currentBuildId: action.id, currentBuildName: action.name };

        case 'LOAD_BUILD': {
            const b = action.build;
            return {
                ...state,
                selectedKnight: b.knight as KnightKey,
                selectedWeapon: b.weapon,
                selectedSpell: b.spell,
                selectedBlessings: new Set(b.blessings),
                currentBuildId: b.id,
                currentBuildName: b.name,
                nimueSelection: b.nimue ?? { option1: null, option2: null },
                anvil: b.anvil ?? { mode: 'single', upgrades: [] },
                morgana: b.morgana ?? { mode: 'single', modifiers: [] },
                merlin: b.merlin ?? { mode: 'single', upgrades: [] },
                source: { ...(state.source), ...(b.source || {}) }, 
                viewedBlessing: null
            };
        }

        case 'NEW_BUILD':
            return {
                ...state,
                selectedKnight: null,
                selectedWeapon: null, // Logic in hook sets this to null when knight is null
                selectedSpell: null,
                selectedBlessings: new Set(),
                currentBuildId: null,
                currentBuildName: null,
                viewedBlessing: null,
                nimueSelection: { option1: null, option2: null },
                anvil: { ...state.anvil, upgrades: [] },
                morgana: { ...state.morgana, modifiers: [] },
                merlin: { ...state.merlin, upgrades: [] },
                source: action.defaultSourceOptions
            };

        default:
            return state;
    }
};
